import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { QueryKeys } from '@/constants/query-keys';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { SlackLogo } from '@gokrew/ui/components/SlackLogo';
import { getInitialsFromName } from '@gokrew/ui/utility/utility';
import styles from './SlackCallbackPage.module.css';

type Status = 'loading' | 'success' | 'error' | 'missing';

export function SlackCallbackPage() {
  const { t } = useTranslation(TranslationNS.CompanyInfo);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyId, companyName, selectedCompany } = useAuth();
  const { token } = useAuth();
  const location = useLocation();

  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(3);
  const called = useRef(false);

  const goToIntegrations = () => navigate('/integrations');

  useEffect(() => {
    if (!token) {
      toast.error(t('slack.callback.errors.session_expired'));
      navigate(
        `/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`
      );
    }
  }, [token]);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const success = searchParams.get('success');
    const slackError = searchParams.get('error');

    if (slackError) {
      const errorKeyMap: Record<string, string> = {
        access_denied: 'slack.callback.errors.access_denied',
        missing_params: 'slack.callback.errors.missing_params',
        invalid_state: 'slack.callback.errors.invalid_state',
        already_linked: 'slack.callback.errors.already_linked',
        // The backend reports every failed token exchange as `server_error`,
        // including a workspace or company that is already connected, so the
        // message names those likely causes rather than a bare "went wrong".
        server_error: 'slack.callback.errors.server_error',
      };
      const message = t(
        errorKeyMap[slackError] ?? 'slack.callback.errors.generic'
      );
      setErrorMsg(message);
      setStatus('error');
      toast.error(message);
      return;
    }

    if (success === 'true') {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.SlackStatus, companyId],
        });
      }, 10000);
      setStatus('success');
      return;
    }

    setStatus('missing');
  }, []);

  useEffect(() => {
    if (status !== 'success') return;
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          goToIntegrations();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  const successFeatures = [
    t('slack.callback.success.features.leave_balance'),
    t('slack.callback.success.features.whos_away'),
    t('slack.callback.success.features.holidays'),
    t('slack.callback.success.features.hr_notifications'),
  ];

  const errorTips = [
    t('slack.callback.error.tips.admin_access'),
    t('slack.callback.error.tips.link_expired'),
  ];

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Brand strip */}
        <div className={styles.brandStrip}>
          <div className={styles.brandSlackIcon}>
            <SlackLogo />
          </div>
          <div className={styles.brandConnector}>
            <span className={styles.brandDot} />
            <span className={styles.brandLine} />
            <span className={styles.brandDot} />
          </div>
          {selectedCompany?.logo ? (
            <div className={styles.brandLogoTile}>
              <img
                src={selectedCompany.logo}
                alt={companyName ?? ''}
                className={styles.brandLogoImg}
              />
            </div>
          ) : (
            <div className={styles.brandCompanyIcon}>
              {getInitialsFromName(companyName ?? '')}
            </div>
          )}
        </div>

        {/* ── LOADING ── */}
        {status === 'loading' && (
          <div className={styles.statusPanel}>
            <Loader2 size={40} className={styles.iconLoading} />
            <div>
              <h2 className={styles.statusTitle}>
                {t('slack.callback.loading.title')}
              </h2>
              <p className={styles.statusDesc}>
                {t('slack.callback.loading.description')}
              </p>
            </div>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {status === 'success' && (
          <div className={styles.statusPanel}>
            <div className={`${styles.iconBubble} ${styles.iconBubbleSuccess}`}>
              <CheckCircle2 size={32} className={styles.iconSuccess} />
            </div>
            <div>
              <h2 className={styles.statusTitle}>
                {t('slack.callback.success.title')}
              </h2>
              <p className={styles.statusDescRelaxed}>
                {t('slack.callback.success.description', { command: '/hr' })}
              </p>
            </div>
            <div className={styles.featurePills}>
              {successFeatures.map((f) => (
                <span key={f} className={styles.featurePill}>
                  {f}
                </span>
              ))}
            </div>
            <p className={styles.countdownText}>
              {t('slack.callback.success.redirecting', { count: countdown })}
            </p>
            <button onClick={goToIntegrations} className={styles.ctaBtn}>
              {t('slack.callback.success.go_to_integrations')}
            </button>
          </div>
        )}

        {/* ── ERROR ── */}
        {status === 'error' && (
          <div className={styles.statusPanel}>
            <div className={`${styles.iconBubble} ${styles.iconBubbleError}`}>
              <XCircle size={32} className={styles.iconError} />
            </div>
            <div>
              <h2 className={styles.statusTitle}>
                {t('slack.callback.error.title')}
              </h2>
              <p className={styles.statusDesc}>{errorMsg}</p>
            </div>
            <div className={styles.tipsList}>
              {errorTips.map((tip) => (
                <div key={tip} className={styles.tipItem}>
                  <AlertCircle size={13} className={styles.tipIcon} />
                  {tip}
                </div>
              ))}
            </div>
            <button onClick={goToIntegrations} className={styles.backBtn}>
              <ArrowLeft size={15} />
              {t('slack.callback.error.back')}
            </button>
          </div>
        )}

        {/* ── MISSING PARAMS ── */}
        {status === 'missing' && (
          <div className={styles.statusPanel}>
            <div className={`${styles.iconBubble} ${styles.iconBubbleWarning}`}>
              <AlertCircle size={32} className={styles.iconWarning} />
            </div>
            <div>
              <h2 className={styles.statusTitle}>
                {t('slack.callback.missing.title')}
              </h2>
              <p className={styles.statusDescRelaxed}>
                {t('slack.callback.missing.description')}
              </p>
            </div>
            <button onClick={goToIntegrations} className={styles.backBtn}>
              <ArrowLeft size={15} />
              {t('slack.callback.missing.back')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
